import { useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaFileDownload } from 'react-icons/fa';
import { IoIdCardOutline } from 'react-icons/io5';

import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import BackButton from '../../../Components/BackButton';
import { fetchWrapper } from '../../../_helpers/fetchWrapper';
import { ErrorMessageHandler } from '../../../_helpers/_methods';
import DefaultColumnFilter from '../../../Components/ReactTable/Filters/DefaultColumnFilter';
import {
  useAsyncDebounce,
  useExpanded,
  useFilters,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable
} from 'react-table';
import ReactTableRender from '../../../Components/ReactTable/ReactTableRender';
import { useRef } from 'react';

// eslint-disable-next-line no-unused-vars
function SuspendedList({ kind, suspended, approved }) {
  const intl = useIntl();

  const permissions = useSelector((state) => state.user.permissions);

  const lang = useSelector((state) => state.local.local);

  const [URL, setURL] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [nameSearch, setNameSearch] = useState('');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [filteredRosters, setFilteredRosters] = useState([]);

  const gotoPage = (page) => setPageNum(page);
  const previousPage = (page) => setPageNum(page);
  const nextPage = (page) => setPageNum(page);

  const handleSuspend = async (e, id, lang) => {
    try {
      let response = await fetchWrapper.get(
        `/profile/resource-person/${id}/unsuspend/`,
        null,
        lang
      );
      if (response) {
        e.target.innerText = intl.formatMessage({ id: 'button.activated' });
      }
    } catch (error) {
      ErrorMessageHandler(error);
    }
  };

  const ManualFilterByName = ({ column: { filterValue, setFilter } }) => {
    const inputRef = useRef(null);
    const [name, setName] = useState(filterValue);
    const handleNameChange = useAsyncDebounce((value) => {
      setFilter(value || undefined);
      setNameSearch(value);
    }, 800);
    return (
      <input
        ref={inputRef}
        value={name || ''}
        onChange={(e) => {
          setName(e.target.value);
          handleNameChange(e.target.value);
        }}
        placeholder={intl.formatMessage({ id: 'filter.search' })}
      />
    );
  };

  const ManualFilterByPhone = ({ column: { filterValue, setFilter } }) => {
    const inputRef = useRef(null);
    const [phone, setPhone] = useState(filterValue);
    const handlePhoneChange = useAsyncDebounce((phone) => {
      setFilter(phone || undefined);
      setPhoneSearch(phone);
    }, 800);
    return (
      <input
        ref={inputRef}
        type="number"
        value={phone || ''}
        onChange={(e) => {
          setPhone(e.target.value);
          handlePhoneChange(e.target.value);
        }}
        placeholder={intl.formatMessage({ id: 'filter.search' })}
      />
    );
  };
  const SelectColumnFilter = ({ column: { filterValue, setFilter, preFilteredRows, id } }) => {
    // Calculate the options for filtering
    // using the preFilteredRows
    const options = useMemo(() => {
      const options = new Set();
      preFilteredRows.forEach((row) => {
        options.add(row.values[id]);
      });
      return [...options.values()];
    }, [id, preFilteredRows]);

    // Render a multi-select box
    return (
      <select
        value={filterValue}
        onChange={(e) => {
          setFilter(e.target.value || undefined);
        }}>
        <FormattedMessage id="tableSelect.all">
          {(label) => <option value="">{label[0]}</option>}
        </FormattedMessage>
        {options.map((option, i) => (
          <option key={i} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  };
  const columns = useMemo(
    () => [
      { Header: <FormattedMessage id="sn" />, accessor: 'serial_number', disableFilters: true },
      {
        Header: <FormattedMessage id="event.fullname" />,
        Filter: ManualFilterByName,
        accessor: 'fullname'
      },
      {
        Header: <FormattedMessage id="address" />,
        accessor: 'address'
      },
      {
        Header: <FormattedMessage id="mobile_number" />,
        Filter: ManualFilterByPhone,
        accessor: 'phone_number'
      },
      {
        Header: <FormattedMessage id="email" />,
        accessor: 'email'
      },

      {
        Header: <FormattedMessage id="kind" />,
        accessor: 'kind',
        Filter: SelectColumnFilter
      },
      {
        Header: <FormattedMessage id="province_priority" />,
        disableFilters: true,
        accessor: 'province_priority'
      },
      {
        Header: <FormattedMessage id="resume" />,
        accessor: 'cv_resume',
        disableFilters: true
      },
      {
        Header: <FormattedMessage id="profile" />,
        accessor: 'profile',
        disableFilters: true
      },
      {
        Header: <FormattedMessage id="actions" />,
        accessor: 'more_options',
        disableFilters: true
      }
    ],
    [lang]
  );
  const data = useMemo(() => {
    if (Array.isArray(filteredRosters)) {
      let count = (pageNum - 1) * 50 + 1;

      return filteredRosters.map((result) => {
        let priority = <div>{result.priorities?.join()}</div>;
        let resume = (
          <div className="d-flex">
            {result.resume ? (
              <button
                className="btn btn-outline-secondary"
                onClick={() => window.open(`${result?.resume}`, '_blank')}>
                <div>
                  <FaFileDownload /> <FormattedMessage id="view" />
                </div>
              </button>
            ) : (
              'N/A'
            )}
          </div>
        );
        let profile = (
          <div>
            <Button>
              <FormattedMessage id="profile" />
            </Button>
          </div>
        );
        let action = permissions['Can change resource person'] && (
          <div className="text-center">
            <Button
              style={{ width: '150px' }}
              variant="outline-info"
              onClick={(e) => handleSuspend(e, result.id, lang)}>
              <FormattedMessage id="button.activate" />
            </Button>
          </div>
        );
        return {
          serial_number: count++,
          fullname: result.fullname,
          email: result.email,
          phone_number: result.phone,
          address: result.address,
          kind: result.type,
          province_priority: priority,
          cv_resume: resume,
          profile: profile,
          more_options: action
        };
      });
    } else {
      return [];
    }
  }, [filteredRosters]);

  const defaultColumn = useMemo(
    () => ({
      // Let's set up our default Filter UI
      Filter: DefaultColumnFilter
    }),
    []
  );

  const {
    headerGroups,
    getTableProps,
    getTableBodyProps,
    page,
    prepareRow,
    visibleColumns,
    setPageSize,
    state: { pageSize, pageIndex }
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 50 },
      manualPagination: true,
      defaultCanFilter: false,
      defaultColumn
    },
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const paramsGenerator = () => {
    let params = '';
    if (pageSize) {
      params += `&limit=${pageSize}`;
    }
    if (pageNum) {
      params += `&offset=${(pageNum - 1) * pageSize}&page=${pageNum}`;
    }
    if (nameSearch) {
      params += `&fullname=${nameSearch}`;
    }
    if (phoneSearch) {
      params += `&phone=${phoneSearch}`;
    }
    return params;
  };
  useEffect(() => {
    let url = '/profile/resource-person/?suspended=true&active_role=resource_person';

    let params = paramsGenerator();
    setURL(`${url}${params}`);
  }, [pageSize, pageNum, nameSearch, phoneSearch]);

  const fetchRosters = async () => {
    try {
      setLoading(true);
      const response = await fetchWrapper.get(URL);
      setTotalCount(response.data.count);
      setFilteredRosters(response.data.results);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
      ErrorMessageHandler(error);
    }
  };

  useEffect(() => {
    if (URL) fetchRosters();
  }, [URL]);

  const pageCountArray = useMemo(
    () => Array.from({ length: Math.ceil(totalCount / pageSize) }, (v, i) => i + 1),
    [totalCount, pageSize]
  );
  return (
    <div className="card">
      <div className="card-header d-flex align-items-center">
        <h3 className="d-flex align-items-center text-primary">
          <IoIdCardOutline className="me-2" />
          <FormattedMessage id="resource_person_suspended_list" />
        </h3>
        <div className="ms-3">
          <BackButton to="/admin/rosters/individual_list" noMargin />
        </div>
      </div>
      <div className="card-body event-wrapper">
        <ReactTableRender
          getTableProps={getTableProps}
          headerGroups={headerGroups}
          getTableBodyProps={getTableBodyProps}
          page={page}
          prepareRow={prepareRow}
          totalCount={totalCount}
          gotoPage={gotoPage}
          previousPage={previousPage}
          pageCount={pageCountArray?.length}
          pagination={true}
          pageCountArray={pageCountArray}
          pageNum={pageNum}
          showGoto={true}
          nextPage={nextPage}
          pageIndex={pageIndex}
          canNextPage={pageNum < pageCountArray?.length}
          canPreviousPage={pageNum > 1}
          loading={loading}
          visibleColumns={visibleColumns}
          pageSize={pageSize}
          setPageSize={setPageSize}
        />
      </div>
    </div>
  );
}

export default SuspendedList;
